import { z } from 'zod';
// ───────────────────────── primitives ─────────────────────────
export const uuidSchema = z.uuid();
export const isoDateSchema = z.iso.date();
export const timestampSchema = z.string(); // ISO-8601 instant, always UTC on the wire
export const currencyCodeSchema = z
    .string()
    .length(3)
    .regex(/^[A-Z]{3}$/, 'Use a three-letter uppercase currency code, e.g. NGN');
export const countryCodeSchema = z
    .string()
    .length(2)
    .regex(/^[A-Z]{2}$/, 'Use a two-letter uppercase country code, e.g. NG');
const DECIMAL_RE = /^-?\d{1,18}(\.\d{1,8})?$/;
/**
 * Decimal input. Accepts a string or number (JSON imports often carry numbers)
 * and normalises to a canonical decimal string. Never goes through float maths.
 */
export const decimalInput = (opts = {}) => z
    .union([z.string(), z.number()])
    .transform((v) => (typeof v === 'number' ? numberToDecimalString(v) : v.trim()))
    .pipe(z
    .string()
    .regex(DECIMAL_RE, 'Enter a valid number')
    .refine((v) => opts.min === undefined || Number(v) >= opts.min, {
    message: `Must be at least ${opts.min}`,
})
    .refine((v) => opts.max === undefined || Number(v) <= opts.max, {
    message: `Must be at most ${opts.max}`,
}));
function numberToDecimalString(n) {
    if (!Number.isFinite(n))
        return 'NaN';
    // toString() yields exponent notation for very large/small values; toFixed avoids it.
    const s = String(n);
    return /e/i.test(s) ? n.toFixed(8).replace(/\.?0+$/, '') : s;
}
export const emptyToUndefined = (schema) => z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? undefined : v), schema);
/**
 * Optional free text. Blank input is stored as null; an *absent* key stays
 * undefined so PATCH requests never wipe fields they did not mention.
 */
export const optionalText = (max = 500) => z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v === undefined ? undefined : v === '' ? null : v))
    .optional();
function stripDefault(schema) {
    const def = schema._zod.def;
    return (def.type === 'default' || def.type === 'prefault') && def.innerType ? stripDefault(def.innerType) : schema;
}
/**
 * Builds a PATCH schema from a create schema: every field optional, and — unlike
 * `.partial()` in Zod 4 — defaults are NOT applied, so omitted fields are left alone.
 */
export function patchOf(schema) {
    const shape = Object.fromEntries(Object.entries(schema.shape).map(([k, v]) => [k, stripDefault(v).optional()]));
    return z.object(shape);
}
// ───────────────────────── pagination ─────────────────────────
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    q: z.string().trim().max(200).optional(),
    sort: z.string().max(50).optional(),
    order: z.enum(['asc', 'desc']).default('desc'),
});
// ───────────────────────── errors ─────────────────────────
export const ERROR_CODES = [
    'validation_failed',
    'unauthenticated',
    'invalid_credentials',
    'mfa_required',
    'mfa_invalid',
    'email_not_verified',
    'account_locked',
    'forbidden',
    'business_access_denied',
    'not_found',
    'conflict',
    'duplicate',
    'rate_limited',
    'csrf_failed',
    'origin_not_allowed',
    'payload_too_large',
    'unsupported_media_type',
    'invoice_not_editable',
    'invoice_not_issuable',
    'invoice_validation_failed',
    'payment_exceeds_balance',
    'nrs_required',
    'integration_unavailable',
    'integration_not_connected',
    'idempotency_conflict',
    'import_invalid_state',
    'file_rejected',
    'extraction_unavailable',
    'internal_error',
];
export const idParamSchema = z.object({ id: uuidSchema });
//# sourceMappingURL=common.js.map